/**
 * ThemeToggle — V2.0 Foundation
 *
 * A compact segmented control that cycles through the three theme options
 * (light / dark / system). Uses `lucide-react` icons already in the codebase.
 *
 * Example (default segmented pill — used inline):
 *   import ThemeToggle from "@/components/v2/ThemeToggle";
 *   <ThemeToggle />
 *
 * Example (compact icon row — used inside dropdown menus and footers):
 *   <ThemeToggle compact />
 *
 * Test IDs:
 *   v2-theme-toggle          — root wrapper
 *   theme-selector           — root wrapper (semantic alias — spec-approved id)
 *   v2-theme-toggle-light    — light button
 *   v2-theme-toggle-dark     — dark button
 *   v2-theme-toggle-system   — system button
 */
import React from "react";
import { Sun, Moon, Monitor } from "lucide-react";
import clsx from "clsx";
import { useTheme } from "@/contexts/ThemeContext";

const OPTIONS = [
  { value: "light",  label: "Light",  Icon: Sun },
  { value: "dark",   label: "Dark",   Icon: Moon },
  { value: "system", label: "System", Icon: Monitor },
];

export default function ThemeToggle({ compact = false }) {
  const { theme, setTheme } = useTheme();

  if (compact) {
    // Icon-only row — fits inside dropdown menus (AppNav / AdminLayout) or
    // the Landing footer. No border wrapper — parent surface owns the frame.
    return (
      <div
        data-testid="theme-selector"
        role="radiogroup"
        aria-label="Theme"
        className="inline-flex items-center gap-0.5"
      >
        {OPTIONS.map(({ value, label, Icon }) => {
          const active = theme === value;
          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={active}
              aria-label={label}
              title={label}
              data-testid={`v2-theme-toggle-${value}`}
              onClick={() => setTheme(value)}
              className={clsx(
                "inline-flex items-center justify-center rounded-v2-md w-7 h-7 transition-colors",
                active
                  ? "bg-v2-accentSoft text-v2-accentStrong"
                  : "text-v2-textMuted hover:text-v2-text hover:bg-v2-surfaceSubtle"
              )}
            >
              <Icon className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div
      data-testid="v2-theme-toggle"
      role="radiogroup"
      aria-label="Theme"
      className="inline-flex items-center gap-1 rounded-v2-lg border border-v2-border bg-v2-surface p-1 shadow-v2-subtle"
    >
      {OPTIONS.map(({ value, label, Icon }) => {
        const active = theme === value;
        return (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={label}
            title={label}
            data-testid={`v2-theme-toggle-${value}`}
            onClick={() => setTheme(value)}
            className={clsx(
              "inline-flex items-center gap-1.5 rounded-v2-md px-2.5 py-1.5 text-v2-body-sm font-medium transition-colors",
              active
                ? "bg-v2-accentSoft text-v2-accentStrong"
                : "text-v2-textMuted hover:text-v2-text hover:bg-v2-surfaceSubtle"
            )}
          >
            <Icon className="h-3.5 w-3.5" aria-hidden="true" />
            <span className="hidden sm:inline">{label}</span>
          </button>
        );
      })}
    </div>
  );
}
